import { useState, useMemo, useEffect } from 'react'
import { Box, VStack, HStack, Text, Avatar, Spinner, IconButton, Button } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { FiEye, FiEyeOff, FiPlus } from 'react-icons/fi'
import type { useItineraryEditor } from '../../../hooks/useItineraryEditor'
import type { ItineraryAssignmentDto } from '../../../types/campaign'
import { DayChipSwitcher, getWeekDates } from './DayChipSwitcher'
import { STATIC_SENTINEL_KEY } from './StaticCellDropZone'
import { DayAgendaAssignmentCard } from './DayAgendaAssignmentCard'
import { AssignTurfSheet } from './AssignTurfSheet'
import { MoveAssignmentSheet } from './MoveAssignmentSheet'
import { AuthedAvatarImage } from '../../shared'

interface ItineraryDayAgendaProps {
  editor: ReturnType<typeof useItineraryEditor>
}

function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function ItineraryDayAgenda({ editor }: ItineraryDayAgendaProps) {
  const { weekStart, isStatic, canvassers, isLoading } = editor

  // Selected day: today if inside the current week, else the week's first day. Static => sentinel.
  const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart])
  const defaultDay = useMemo(() => {
    const today = toLocalDateStr(new Date())
    return weekDates.some(d => d.dateStr === today) ? today : weekDates[0]?.dateStr ?? weekStart
  }, [weekDates, weekStart])
  const [selectedDate, setSelectedDate] = useState(defaultDay)

  // Resync selectedDate whenever it falls outside the currently displayed week
  // (e.g. after goToPreviousWeek/goToNextWeek), so dayKey/assignTurf never target a stale date.
  useEffect(() => {
    if (!weekDates.some(d => d.dateStr === selectedDate)) setSelectedDate(defaultDay)
  }, [weekStart]) // eslint-disable-line react-hooks/exhaustive-deps

  const dayKey = isStatic ? STATIC_SENTINEL_KEY : selectedDate
  const [moveTarget, setMoveTarget] = useState<ItineraryAssignmentDto | null>(null)
  const [assignForUser, setAssignForUser] = useState<string | null>(null)

  if (isLoading) {
    return <VStack py={8}><Spinner size="lg" color="accent" /><Text color="gray.500" fontSize="sm">Loading assignments…</Text></VStack>
  }

  return (
    <VStack gap={4} align="stretch">
      {!isStatic && (
        <DayChipSwitcher
          weekStart={weekStart}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
          onPrevWeek={editor.goToPreviousWeek}
          onNextWeek={editor.goToNextWeek}
          onToday={() => { editor.goToToday(); setSelectedDate(toLocalDateStr(new Date())) }}
        />
      )}

      {canvassers.length === 0 && (
        <Text fontSize="sm" color="gray.400" textAlign="center" py={6}>No canvassers in this committee</Text>
      )}

      {canvassers.map(c => {
        const assignments = c.dayAssignments[dayKey] ?? []
        return (
          <Box key={c.grantId} bg="surface" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3}>
            <HStack justify="space-between" gap={2} mb={2}>
              <HStack gap={2}>
                <Avatar.Root size="sm"><Avatar.Fallback name={c.fullName} /><AuthedAvatarImage src={c.portraitUrl} /></Avatar.Root>
                <Text fontWeight="semibold" fontSize="sm">{c.fullName}</Text>
              </HStack>
              {!isStatic && (() => {
                const isAvailable = c.availability?.[dayKey] !== false
                return (
                  <Tooltip contentProps={{ fontSize: "xs" }} content={isAvailable ? 'Mark unavailable' : 'Mark available'} showArrow>
                    <IconButton
                      aria-label={isAvailable ? 'Mark unavailable' : 'Mark available'}
                      size="sm"
                      variant="ghost"
                      minW="44px"
                      h="44px"
                      color={isAvailable ? 'gray.400' : 'red.400'}
                      onClick={() => editor.toggleAvailability(c.grantId, dayKey, !isAvailable)}>{isAvailable ? <FiEye /> : <FiEyeOff />}</IconButton>
                  </Tooltip>
                );
              })()}
            </HStack>
            <VStack gap={2} align="stretch">
              {assignments.map(a => (
                <DayAgendaAssignmentCard
                  key={a.id}
                  assignment={a}
                  onUnassign={editor.removeAssignment}
                  onMove={(assignment) => setMoveTarget(assignment)}
                />
              ))}
              {assignments.length === 0 && (
                <Text fontSize="xs" color="gray.400">No turfs assigned</Text>
              )}
              <Button
                size="sm"
                variant="outline"
                colorPalette="brand"
                w="full"
                minH="44px"
                onClick={() => setAssignForUser(c.grantId)}><FiPlus />Assign turf
                              </Button>
            </VStack>
          </Box>
        );
      })}

      <AssignTurfSheet
        isOpen={assignForUser !== null}
        onClose={() => setAssignForUser(null)}
        turfs={editor.poolTurfs}
        onSelect={(turf) => { if (assignForUser) editor.assignTurf(turf, assignForUser, dayKey) }}
      />
      <MoveAssignmentSheet
        isOpen={moveTarget !== null}
        onClose={() => setMoveTarget(null)}
        assignment={moveTarget}
        canvassers={editor.canvassers}
        weekStart={editor.weekStart}
        isStatic={editor.isStatic}
        onMove={(targetUserId, targetDateStr) => { if (moveTarget) editor.moveAssignment(moveTarget, targetUserId, targetDateStr) }}
      />
    </VStack>
  );
}
